/* ──────────────────────────────────────────────────────────────────────────
 * Document Vault — reusable panel (body of the standalone Document Vault).
 *
 * LIVE per-workspace vault. Fetches the SEBI checklist + document statuses from
 * the backend (/api/workspace/vault), supports real uploads (GridFS, ≤20MB),
 * downloads, status changes and an AI assistant for the SEBI / gap actions.
 * Rendered both as the standalone `/document-vault` page and embedded inside the
 * IPO Navigator "Document Vault" screen. Must be inside a `.doc-vault-root`
 * ancestor (scoped CSS).
 * ────────────────────────────────────────────────────────────────────────── */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  approved: { badge: "ds-done", label: "Approved", iconBg: "#E1F5EE", iconColor: "#0F6E56", icon: "ti-file-check" },
  review: { badge: "ds-review", label: "Under review", iconBg: "#FAEEDA", iconColor: "#854F0B", icon: "ti-file-search" },
  missing: { badge: "ds-missing", label: "Missing", iconBg: "#FCEBEB", iconColor: "#A32D2D", icon: "ti-file-alert" },
  draft: { badge: "ds-draft", label: "In draft", iconBg: "#E6F1FB", iconColor: "#185FA5", icon: "ti-file-text" },
  locked: { badge: "ds-locked", label: "Locked", iconBg: "#F1EFE8", iconColor: "#5F5E5A", icon: "ti-lock" },
};
const metaFor = (s) => STATUS_META[s] || STATUS_META.missing;

const catBadgeClass = (cat) => {
  if (cat.complete === cat.total) return "ok";
  if (cat.missing >= 2) return "err";
  return "warn";
};

const DocumentVaultPanel = ({ embedded = false }) => {
  const [vault, setVault] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [dpTab, setDpTab] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [ai, setAi] = useState({ open: false, loading: false, title: "", text: "" });

  const fileRef = useRef(null);
  const uploadTarget = useRef(null);

  const reload = useCallback(() => {
    fetch(`${API_URL}/api/workspace/vault`, { credentials: "include" })
      .then((r) => r.json())
      .then(setVault)
      .catch(() => {});
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const categories = vault?.categories || [];
  const totals = vault?.totals || { total: 0, approved: 0, review: 0, missing: 0, locked: 0, complete: 0, pct: 0 };

  const visibleCategories = useMemo(() => {
    const cats = vault?.categories || [];
    const q = query.trim().toLowerCase();
    const matchStatus = (d) =>
      filter === "all" ? true
        : filter === "needs-action" ? d.status === "missing"
        : filter === "review" ? d.status === "review"
        : filter === "approved" ? d.status === "approved"
        : true;
    return cats
      .filter((c) => (filter.startsWith("cat-") ? c.key === filter : true))
      .map((c) => ({
        ...c,
        docs: c.docs.filter((d) =>
          matchStatus(d) &&
          (!q || d.title.toLowerCase().includes(q) || (d.owner || "").toLowerCase().includes(q) || (d.remarks || "").toLowerCase().includes(q))
        ),
      }))
      .filter((c) => c.docs.length > 0);
  }, [vault, filter, query]);

  const pct = totals.total ? Math.round((totals.complete / totals.total) * 100) : 0;

  // ── actions ──────────────────────────────────────────────────────────────
  const triggerUpload = (id) => { uploadTarget.current = id; setTimeout(() => fileRef.current?.click(), 0); };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const id = uploadTarget.current;
    if (!file || !id) return;
    setBusyId(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${API_URL}/api/workspace/vault/${id}/upload`, { method: "POST", credentials: "include", body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) toast.error(j.detail || "Upload failed");
      else { toast.success("Document uploaded"); reload(); }
    } catch { toast.error("Upload failed"); }
    setBusyId(null);
    uploadTarget.current = null;
  };

  const download = async (d) => {
    try {
      const r = await fetch(`${API_URL}/api/workspace/vault/${d.id}/download`, { credentials: "include" });
      if (!r.ok) { toast.error("Download failed"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.file?.filename || d.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const setStatus = async (id, status) => {
    try {
      const r = await fetch(`${API_URL}/api/workspace/vault/${id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }),
      });
      if (r.ok) { reload(); } else { toast.error("Could not update status"); }
    } catch { toast.error("Could not update status"); }
  };

  const removeFile = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/workspace/vault/${id}/file`, { method: "DELETE", credentials: "include" });
      if (r.ok) { toast.success("File removed"); reload(); } else { toast.error("Could not remove file"); }
    } catch { toast.error("Could not remove file"); }
  };

  const askAI = async (mode, item) => {
    const title = mode === "requirement" ? `SEBI requirement — ${item?.title}`
      : mode === "gaps" ? "Action plan to close document gaps"
      : "About your Document Vault";
    setAi({ open: true, loading: true, title, text: "" });
    try {
      const r = await fetch(`${API_URL}/api/workspace/vault/assistant`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, item_id: item?.id }),
      });
      const j = await r.json().catch(() => ({}));
      setAi((a) => ({ ...a, loading: false, text: r.ok ? (j.answer || "No answer.") : (j.detail || "AI assistant failed.") }));
    } catch { setAi((a) => ({ ...a, loading: false, text: "AI assistant failed. Please try again." })); }
  };

  if (!vault) {
    return <div style={{ padding: 28, fontSize: 13, color: "var(--color-text-secondary)" }}>Loading your document vault…</div>;
  }

  return (
    <>
      <input ref={fileRef} type="file" style={{ display: "none" }} onChange={onFileChosen}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp" data-testid="dv-file-input" />

      {!embedded && (
        <h2 className="sr-only">
          IPO Navigator — Document Vault. A SEBI-checklist-driven document management system tracking all
          filings, with version control, completion status and secure storage for Indian IPO preparation.
        </h2>
      )}

      <div className="dv-header">
        {!embedded && (
          <>
            <div className="dv-title">Document Vault</div>
            <div className="dv-sub">Every document your IPO needs — organised by SEBI mandate, tracked by status and stored securely for your company.</div>
          </>
        )}

        <div className="stat-row">
          <div className="stat-box"><div className="stat-val" style={{ color: "#185FA5" }}>{totals.total}</div><div className="stat-lbl">Total documents required</div></div>
          <div className="stat-box"><div className="stat-val" style={{ color: "#1D9E75" }}>{totals.approved}</div><div className="stat-lbl">Complete &amp; approved</div></div>
          <div className="stat-box"><div className="stat-val" style={{ color: "#BA7517" }}>{totals.review}</div><div className="stat-lbl">Under review</div></div>
          <div className="stat-box"><div className="stat-val" style={{ color: "#A32D2D" }}>{totals.missing}</div><div className="stat-lbl">Missing / not uploaded</div></div>
          <div className="stat-box"><div className="stat-val" style={{ color: "#5F5E5A" }}>{totals.locked}</div><div className="stat-lbl">Locked post-approval</div></div>
        </div>

        {totals.missing > 0 && (
          <div className="alert">
            <i className="ti ti-alert-triangle" style={{ fontSize: 16, color: "#BA7517" }} aria-hidden="true" />
            <div className="alert-t">
              <strong>{totals.missing}</strong> documents are still missing and may block your DRHP filing. Upload them below, or get an AI-generated action plan to close the gaps fast.
            </div>
            <button className="alert-btn" onClick={() => askAI("explain")} data-testid="dv-ai-explain">Explain these docs ↗</button>
          </div>
        )}
      </div>

      <div className="dv-body">
        {/* Sidebar */}
        <div className="sidebar">
          <div className="sb-sec">
            <div className="sb-lbl">All documents</div>
            <div className={`sb-item ${filter === "all" ? "on" : ""}`} onClick={() => setFilter("all")} data-testid="sb-all">
              <i className="ti ti-layout-grid" style={{ fontSize: 14 }} aria-hidden="true" /> All categories <span className="sb-badge info">{totals.total}</span>
            </div>
            <div className={`sb-item ${filter === "needs-action" ? "on" : ""}`} onClick={() => setFilter("needs-action")} data-testid="sb-needs-action">
              <i className="ti ti-alert-circle" style={{ fontSize: 14 }} aria-hidden="true" /> Needs action <span className="sb-badge err">{totals.missing}</span>
            </div>
            <div className={`sb-item ${filter === "review" ? "on" : ""}`} onClick={() => setFilter("review")} data-testid="sb-review">
              <i className="ti ti-clock" style={{ fontSize: 14 }} aria-hidden="true" /> Under review <span className="sb-badge warn">{totals.review}</span>
            </div>
            <div className={`sb-item ${filter === "approved" ? "on" : ""}`} onClick={() => setFilter("approved")} data-testid="sb-approved">
              <i className="ti ti-circle-check" style={{ fontSize: 14 }} aria-hidden="true" /> Approved <span className="sb-badge ok">{totals.approved}</span>
            </div>
          </div>

          <div className="sb-sec">
            <div className="sb-lbl">By category</div>
            {categories.map((c) => (
              <div key={c.key} className={`sb-item ${filter === c.key ? "on" : ""}`} onClick={() => setFilter(c.key)} title={c.name} data-testid={`sb-${c.key}`}>
                <i className={`ti ${c.icon}`} style={{ fontSize: 14 }} aria-hidden="true" /> {c.short}
                <span className={`sb-badge ${catBadgeClass(c)}`}>{c.complete}/{c.total}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: 14 }}>
            <button className="tb-btn pri" style={{ width: "100%", justifyContent: "center" }} onClick={() => askAI("explain")} data-testid="dv-ai-checklist">
              <i className="ti ti-bulb" style={{ fontSize: 13 }} aria-hidden="true" /> Explain the SEBI checklist ↗
            </button>
          </div>
        </div>

        {/* Main */}
        <div className="main">
          <div className="toolbar">
            <div className="search-box">
              <i className="ti ti-search" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
              <input type="text" placeholder="Search documents, owners, tags..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="dv-search" />
            </div>
            <button className="tb-btn" onClick={() => setFilter("needs-action")} data-testid="dv-show-missing"><i className="ti ti-filter" style={{ fontSize: 13 }} aria-hidden="true" /> Show missing</button>
            <button className="tb-btn pri" onClick={() => askAI("gaps")} data-testid="dv-ai-gaps-top"><i className="ti ti-sparkles" style={{ fontSize: 13 }} aria-hidden="true" /> AI gap plan ↗</button>
          </div>

          <div className="progress-track">
            <div className="pt-label">Overall completion</div>
            <div className="pt-bar-wrap"><div className="pt-bar" style={{ width: `${pct}%`, background: "#185FA5" }} /></div>
            <div className="pt-num" style={{ color: "#185FA5" }}>{pct}%</div>
            <div className="pt-frac">{totals.complete} / {totals.total}</div>
          </div>

          <div className="col-header">
            <div className="col-h"></div>
            <div className="col-h">Document</div>
            <div className="col-h">Owner</div>
            <div className="col-h">Last updated</div>
            <div className="col-h" style={{ textAlign: "right" }}>Actions</div>
          </div>

          {visibleCategories.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "24px 0", textAlign: "center" }}>No documents match this view.</div>
          ) : (
            visibleCategories.map((cat) => {
              const cls = catBadgeClass(cat);
              const badgeStyle =
                cls === "ok" ? { background: "#E1F5EE", color: "#085041" }
                  : cls === "err" ? { background: "#FCEBEB", color: "#791F1F" }
                  : { background: "#FAEEDA", color: "#854F0B" };
              return (
                <div key={cat.key}>
                  <div className="cat-label" style={{ marginTop: 18 }}>
                    <span>{cat.name}</span>
                    <span style={{ fontSize: 10, ...badgeStyle, padding: "1px 8px", borderRadius: 8, fontWeight: 500 }}>
                      {cat.complete} of {cat.total} complete{cls === "err" ? " — action needed" : ""}
                    </span>
                    <div className="cat-line"></div>
                  </div>

                  {cat.docs.map((d) => {
                    const meta = metaFor(d.status);
                    const busy = busyId === d.id;
                    return (
                      <div key={d.id}>
                        <div className="doc-row" onClick={() => setExpanded(expanded === d.id ? null : d.id)} data-testid={`doc-row-${d.id}`}>
                          <div className="doc-icon" style={{ background: meta.iconBg }}>
                            <i className={`ti ${meta.icon}`} style={{ fontSize: 14, color: meta.iconColor }} aria-hidden="true" />
                          </div>
                          <div style={{ padding: "0 10px 0 8px" }}>
                            <div className="doc-name" style={{ padding: 0 }}>{d.title}</div>
                            <div className="doc-name-sub">
                              {d.remarks}
                              {d.mandatory && (
                                <span className="sebi-req"><i className="ti ti-shield" style={{ fontSize: 9 }} aria-hidden="true" /> SEBI mandatory</span>
                              )}
                            </div>
                          </div>
                          <div className="doc-owner" style={d.status === "missing" ? { color: "#A32D2D" } : undefined}>{d.owner}</div>
                          <div className="doc-date" style={d.status === "missing" ? { color: "#A32D2D" } : undefined}>{d.date}</div>
                          <div className="doc-actions">
                            <div className="act-icon" onClick={(e) => { e.stopPropagation(); triggerUpload(d.id); }} aria-label="Upload" title={d.file ? "Replace file" : "Upload file"} data-testid={`dv-upload-${d.id}`}>
                              <i className={`ti ${busy ? "ti-loader-2" : "ti-upload"}`} style={{ fontSize: 13, ...(busy ? { animation: "spin 1s linear infinite" } : {}) }} aria-hidden="true" />
                            </div>
                            <div className="act-icon" onClick={(e) => { e.stopPropagation(); setExpanded(expanded === d.id ? null : d.id); }} aria-label="View details">
                              <i className="ti ti-eye" style={{ fontSize: 13 }} aria-hidden="true" />
                            </div>
                            {d.file && (
                              <div className="act-icon" onClick={(e) => { e.stopPropagation(); download(d); }} aria-label="Download" title="Download" data-testid={`dv-download-${d.id}`}>
                                <i className="ti ti-download" style={{ fontSize: 13 }} aria-hidden="true" />
                              </div>
                            )}
                          </div>
                        </div>

                        {expanded === d.id && (
                          <div data-testid={`doc-detail-${d.id}`}>
                            <div className="detail-panel">
                              <div className="dp-header">
                                <div className="dp-icon" style={{ background: meta.iconBg }}>
                                  <i className={`ti ${meta.icon}`} style={{ fontSize: 18, color: meta.iconColor }} aria-hidden="true" />
                                </div>
                                <div>
                                  <div className="dp-title">{d.title}</div>
                                  <div className="dp-sub">{d.file ? `${d.file.filename} · uploaded ${d.date}` : "Not yet uploaded"}</div>
                                  <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
                                    <span className={`doc-status ${meta.badge}`} style={{ fontSize: 10 }}>{meta.label}</span>
                                    {d.mandatory && <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 8, background: "#EEEDFE", color: "#3C3489", fontWeight: 500 }}>SEBI ICDR mandate</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="tab-row">
                                {["Details", "Version history", "SEBI notes"].map((t, i) => (
                                  <div key={t} className={`dp-tab ${dpTab === i ? "on" : ""}`} onClick={() => setDpTab(i)}>{t}</div>
                                ))}
                              </div>
                              <div className="meta-grid">
                                <div className="meta-item"><div className="meta-lbl">Uploaded by</div><div className="meta-val">{d.file?.uploaded_by || "Not uploaded"}</div></div>
                                <div className="meta-item"><div className="meta-lbl">Status</div><div className="meta-val">{meta.label}</div></div>
                                <div className="meta-item"><div className="meta-lbl">Category</div><div className="meta-val">{cat.short}</div></div>
                                <div className="meta-item"><div className="meta-lbl">Mandate</div><div className="meta-val">{d.mandatory ? "SEBI mandatory" : "Conditional"}</div></div>
                              </div>
                              <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-tertiary)", fontWeight: 500, marginBottom: 8 }}>Stored file</div>
                              {d.file ? (
                                <div className="version-row"><span className="v-tag cur">Current</span><div className="v-who">{d.file.uploaded_by} — {d.file.filename}</div><div className="v-date">{d.date}</div></div>
                              ) : (
                                <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>No file uploaded yet. Use “Upload file” to add the document.</div>
                              )}
                              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                <button className="tb-btn" onClick={() => setExpanded(null)}>Close</button>
                                <button className="tb-btn" onClick={() => triggerUpload(d.id)} data-testid={`dv-detail-upload-${d.id}`}>{d.file ? "Replace file" : "Upload file"} ↗</button>
                                {d.file && <button className="tb-btn" onClick={() => download(d)}>Download</button>}
                                {d.file && d.status !== "approved" && <button className="tb-btn" onClick={() => setStatus(d.id, "approved")} data-testid={`dv-approve-${d.id}`}>Mark approved</button>}
                                {d.file && <button className="tb-btn" onClick={() => removeFile(d.id)}>Delete</button>}
                                <button className="tb-btn pri" onClick={() => askAI("requirement", d)} data-testid={`dv-ai-req-${d.id}`}>SEBI requirements ↗</button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}

          {totals.missing > 0 && (
            <div style={{ marginTop: 14, display: "flex", gap: 10, paddingTop: 12, borderTop: "0.5px solid var(--color-border-tertiary)", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", flex: 1 }}>
                {totals.missing} missing or incomplete documents are blocking DRHP filing. Get an AI-generated action plan to close gaps fast.
              </div>
              <button className="tb-btn pri" onClick={() => askAI("gaps")} data-testid="dv-ai-gaps">Resolve all gaps ↗</button>
            </div>
          )}
        </div>
      </div>

      {ai.open && (
        <div onClick={() => setAi((a) => ({ ...a, open: false }))} data-testid="vault-ai-overlay"
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} data-testid="vault-ai-modal"
            style={{ background: "#fff", borderRadius: 14, maxWidth: 660, width: "100%", maxHeight: "82vh", overflow: "auto", boxShadow: "0 24px 60px rgba(0,0,0,0.28)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px", borderBottom: "1px solid #eee", position: "sticky", top: 0, background: "#fff" }}>
              <i className="ti ti-sparkles" style={{ fontSize: 18, color: "#185FA5" }} aria-hidden="true" />
              <div style={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{ai.title}</div>
              <div className="act-icon" onClick={() => setAi((a) => ({ ...a, open: false }))} data-testid="vault-ai-close" style={{ cursor: "pointer" }}>
                <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden="true" />
              </div>
            </div>
            <div style={{ padding: "18px 20px", fontSize: 13.5, lineHeight: 1.6, whiteSpace: "pre-wrap", color: "#1f2937" }} data-testid="vault-ai-text">
              {ai.loading ? "Thinking…" : ai.text}
            </div>
            <div style={{ padding: "0 20px 14px", fontSize: 10.5, color: "#9aa0a6" }}>AI-generated guidance · always verify against SEBI ICDR and your advisors.</div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentVaultPanel;
